import type { VolumePoolFragment } from "~/gql/queries/volumePools.generated";

import { PriceKind, PriceLevelInputDataInput, PriceLevelType, TargetType } from "~/gql/graphqlBase.generated";
import { isValidVolumePoolCode } from "~/helpers/fieldValidators";
import { findVolumePoolByName } from "~fulfill/sales/pricing/helpers/poolLadderScope";

export const isValidVolumePoolSelection = (poolName: string, pools: readonly VolumePoolFragment[]): boolean => {
	const trimmedPoolName = poolName.trim();
	return !trimmedPoolName || !!findVolumePoolByName(pools, trimmedPoolName) || isValidVolumePoolCode(trimmedPoolName);
};

export type PriceLevelFormErrors = Partial<Record<keyof PriceLevelInputDataInput, string>>;

export interface PriceLevelIdentity {
	type: PriceLevelInputDataInput["type"];
	targetId: string;
	targetName?: string | null;
	quantityThreshold: number;
	startDate?: string | null;
	endDate?: string | null;
	kind?: PriceKind | null;
	levelType?: PriceLevelType | null;
}

export const END_BEFORE_START_MESSAGE = "End date must be on or after the start date";
export const DUPLICATE_LEVEL_MESSAGE =
	"A price level with the same target, quantity threshold, and dates already exists";
export const PERCENT_OFF_REQUIRED_MESSAGE = "Percent off must be greater than 0 and at most 100";
export const REFERENCE_GROUP_REQUIRED_MESSAGE = "Reference price group is required for a percent-off level";
export const REFERENCE_GROUP_SELF_MESSAGE = "A price level cannot reference its own price group";
export const REFERENCE_GROUP_INACTIVE_MESSAGE = "The referenced price group is no longer active — pick another";
export const CATEGORY_VOLUME_REQUIRES_FIXED_MESSAGE =
	"Category volume levels must be Fixed — percent off is not supported yet";

export type ReferencePriceGroupIssue = "inactive" | "self";

export const findReferencePriceGroupIssue = (
	referencePriceGroupId: string | null | undefined,
	ownPriceGroupId?: string | null,
	activeReferenceGroupIds?: ReadonlySet<string>
): ReferencePriceGroupIssue | null => {
	if (!referencePriceGroupId?.trim()) return null;
	if (ownPriceGroupId && referencePriceGroupId === ownPriceGroupId) return "self";
	if (activeReferenceGroupIds && !activeReferenceGroupIds.has(referencePriceGroupId)) return "inactive";
	return null;
};

export const isEndBeforeStart = (startDate?: string | null, endDate?: string | null): boolean => {
	if (!startDate || !endDate) return false;
	return new Date(endDate).getTime() < new Date(startDate).getTime();
};

export const priceLevelIdentityKey = (level: PriceLevelIdentity): string => {
	const start = (level.startDate ?? "").trim();
	const end = (level.endDate ?? "").trim();
	const kind = level.kind ?? PriceKind.Fixed;
	const levelType = level.levelType ?? PriceLevelType.LinePrice;
	return `${level.type}-${level.targetId.trim().toLowerCase()}-${level.quantityThreshold}-${start}-${end}-${kind}-${levelType}`;
};

export interface PriceLevelCandidate extends PriceLevelIdentity {
	priceLevelId: string;
}

// A CATEGORY level whose targetId is now a guid still duplicates a candidate for the same code carried in its targetName.
const matchesCandidateIdentity = (existing: PriceLevelIdentity, candidateKey: string): boolean => {
	if (priceLevelIdentityKey(existing) === candidateKey) return true;
	if (existing.type !== TargetType.Category || !existing.targetName) return false;
	return priceLevelIdentityKey({ ...existing, targetId: existing.targetName }) === candidateKey;
};

export const findDuplicatePriceLevel = (
	candidate: PriceLevelIdentity,
	levels: readonly PriceLevelCandidate[],
	excludePriceLevelId?: string | null
): boolean => {
	const candidateKey = priceLevelIdentityKey(candidate);
	return levels.some(
		(level) => level.priceLevelId !== excludePriceLevelId && matchesCandidateIdentity(level, candidateKey)
	);
};

const validatePriceLevelForm = (
	formData: PriceLevelInputDataInput,
	otherLevels: readonly PriceLevelIdentity[] = [],
	currentPriceGroupId?: string | null,
	activeReferenceGroupIds?: ReadonlySet<string>
): PriceLevelFormErrors => {
	const errors: PriceLevelFormErrors = {};

	if (!formData.targetName.trim()) {
		errors.targetName = "Target name is required";
	}
	if (!formData.targetId.trim()) {
		errors.targetId = "Target ID is required";
	}
	if (formData.quantityThreshold < 0) {
		errors.quantityThreshold = "Quantity threshold must be non-negative";
	}
	if (formData.growerCost != null && formData.growerCost < 0) {
		errors.growerCost = "Grower cost must be non-negative";
	}
	if (formData.growerSellPrice < 0) {
		errors.growerSellPrice = "Grower sell price must be non-negative";
	}
	if (formData.customerSellPrice != null && formData.customerSellPrice < 0) {
		errors.customerSellPrice = "Customer sell price must be non-negative";
	}

	if (isEndBeforeStart(formData.startDate, formData.endDate)) {
		errors.endDate = END_BEFORE_START_MESSAGE;
	}

	if (formData.kind === PriceKind.PercentOff && formData.levelType === PriceLevelType.CategoryVolume) {
		errors.levelType = CATEGORY_VOLUME_REQUIRES_FIXED_MESSAGE;
	}

	if (formData.kind === PriceKind.PercentOff) {
		if (formData.percentOff == null || formData.percentOff <= 0 || formData.percentOff > 100) {
			errors.percentOff = PERCENT_OFF_REQUIRED_MESSAGE;
		}
		if (!formData.referencePriceGroupId?.trim()) {
			errors.referencePriceGroupId = REFERENCE_GROUP_REQUIRED_MESSAGE;
		} else {
			const referenceIssue = findReferencePriceGroupIssue(
				formData.referencePriceGroupId,
				currentPriceGroupId,
				activeReferenceGroupIds
			);
			if (referenceIssue === "self") {
				errors.referencePriceGroupId = REFERENCE_GROUP_SELF_MESSAGE;
			} else if (referenceIssue === "inactive") {
				errors.referencePriceGroupId = REFERENCE_GROUP_INACTIVE_MESSAGE;
			}
		}
	}

	if (formData.targetId.trim()) {
		const candidateKey = priceLevelIdentityKey(formData);
		if (otherLevels.some((level) => matchesCandidateIdentity(level, candidateKey))) {
			errors.targetId = DUPLICATE_LEVEL_MESSAGE;
		}
	}

	return errors;
};

export default validatePriceLevelForm;
